import { z } from "zod";
import {
  CreateAmendmentRequestValidation,
  CreateContractValidation,
} from "@/lib/api/otc";

export const OtcLanguagePreviewTypeValidation = z.enum([
  "amendment",
  "invoice",
]);

const OtcLanguagePreviewAmendmentBaseValidation =
  CreateAmendmentRequestValidation.omit({
    expected_current_layer_id: true,
  }).strict();

export const OtcLanguagePreviewAmendmentValidation =
  OtcLanguagePreviewAmendmentBaseValidation.superRefine(
    (
      amendment: z.infer<
        typeof OtcLanguagePreviewAmendmentBaseValidation
      >,
      context: z.RefinementCtx,
    ): void => {
      if (
        amendment.amendment_type === "roll" &&
        amendment.roll_direction === undefined
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Roll direction is required for a roll amendment",
          path: ["roll_direction"],
        });
      }

      if (
        amendment.amendment_type !== "roll" &&
        amendment.roll_direction !== undefined
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Roll direction is only valid for a roll amendment",
          path: ["roll_direction"],
        });
      }
    },
  );

const ExistingLayerPreviewValidation = z
  .object({
    type: OtcLanguagePreviewTypeValidation,
    layer_id: z.string().uuid(),
  })
  .strict();

const ExistingLayerAmendmentPreviewValidation = z
  .object({
    type: OtcLanguagePreviewTypeValidation,
    layer_id: z.string().uuid(),
    amendment: OtcLanguagePreviewAmendmentValidation,
  })
  .strict();

const SampleContractAmendmentPreviewValidation = z
  .object({
    type: OtcLanguagePreviewTypeValidation,
    sample_contract: CreateContractValidation,
    amendment: OtcLanguagePreviewAmendmentValidation,
  })
  .strict();

export const OtcLanguagePreviewRequestValidation = z.union([
  ExistingLayerPreviewValidation,
  ExistingLayerAmendmentPreviewValidation,
  SampleContractAmendmentPreviewValidation,
]);

export const OtcBaseContractLanguagePreviewRequestValidation = z.union([
  z
    .object({
      layer_id: z.string().uuid(),
    })
    .strict(),
  z
    .object({
      sample_contract: CreateContractValidation,
    })
    .strict(),
]);

export const OtcLanguagePreviewDataValidation = z.object({
  language: z.string(),
});

export type OtcLanguagePreviewType = z.infer<
  typeof OtcLanguagePreviewTypeValidation
>;
export type OtcLanguagePreviewAmendment = z.infer<
  typeof OtcLanguagePreviewAmendmentValidation
>;
export type OtcLanguagePreviewRequest = z.infer<
  typeof OtcLanguagePreviewRequestValidation
>;
export type OtcBaseContractLanguagePreviewRequest = z.infer<
  typeof OtcBaseContractLanguagePreviewRequestValidation
>;
export type OtcContractCreatePayload = z.infer<
  typeof CreateContractValidation
>;
export type OtcLanguagePreviewData = z.infer<
  typeof OtcLanguagePreviewDataValidation
>;
